<template>
  <v-container fluid class="pa-0 admin-wl-wrap">
    <!-- App Bar -->
    <v-app-bar flat dark class="grad-appbar">
      <v-toolbar-title style="color: #6B8BA4;" class="font-weight-bold">Waitlist</v-toolbar-title>
      <v-spacer />
      <v-text-field
        v-model="search"
        dense hide-details solo-inverted flat prepend-inner-icon="mdi-magnify"
        placeholder="Search name, email, @username, city, branch, certs…"
        class="mr-3 search-input contrast-input"
      />
      <v-btn small class="chip-btn mr-2" @click="exportCsv">
        <v-icon left small>mdi-download</v-icon> Export CSV
      </v-btn>
      <v-btn icon @click="refresh"><v-icon>mdi-refresh</v-icon></v-btn>
    </v-app-bar>

    <!-- Filters / Counts -->
    <v-container class="pt-4">
      <v-row>
        <v-col cols="12" md="8">
          <div class="d-flex flex-wrap">
            <v-btn
              v-for="opt in statusOptions"
              :key="opt.value"
              small rounded class="chip-btn mr-2 mb-2"
              :outlined="statusFilter !== opt.value"
              @click="statusFilter = opt.value"
            >
              <v-icon left small>{{ opt.icon }}</v-icon>{{ opt.label }}
              <span v-if="counts[opt.value] !== undefined" class="ml-2 badge">{{ counts[opt.value] }}</span>
            </v-btn>

            <v-btn
              small rounded class="chip-btn mr-2 mb-2"
              :outlined="!onlyCompleted"
              @click="onlyCompleted = !onlyCompleted"
            >
              <v-icon left small>mdi-account-check</v-icon> Completed
            </v-btn>
          </div>
        </v-col>
        <v-col cols="12" md="4" class="text-md-right">
          <span class="caption dark--text opacity-80">
            Showing {{ filteredApplicants.length }} of {{ applicants.length }}
          </span>
        </v-col>
      </v-row>
    </v-container>

    <!-- Table -->
    <v-container>
      <v-card class="soft-card dark-surface" rounded="xl" dark>
        <v-data-table
          :headers="headers"
          :items="filteredApplicants"
          :loading="loading"
          class="elevation-0 wl-table"
          item-key="uid"
          disable-pagination
          dense
          dark
        >
          <template #progress>
            <v-progress-linear indeterminate color="yellow" height="3" />
          </template>

          <!-- Applicant -->
          <template #item.fullName="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="28" class="mr-2">
                <v-img :src="item.image || item.user?.image" :lazy-src="defaultImage" cover>
                  <template #error>
                    <div class="avatar-fallback">{{ initials(item.fullName) }}</div>
                  </template>
                </v-img>
              </v-avatar>
              <div>
                <div class="row-title">{{ item.fullName || '—' }}</div>
                <div class="caption row-subtitle">{{ item.email }}</div>
              </div>
            </div>
          </template>

          <!-- Handle -->
          <template #item.username="{ item }">
            <span class="row-subtitle" v-if="item.username">@{{ item.username }}</span>
            <span class="row-subtitle" v-else>—</span>
          </template>

          <!-- Role (keep if you use it) -->
          <template #item.ownerRole="{ item }">
            <span class="row-subtitle">{{ item.ownerRole || '—' }}</span>
          </template>

          <!-- Service (NEW) -->
          <template #item.service="{ item }">
            <div class="row-subtitle">
              <v-chip x-small class="mr-1" color="primary" text-color="white" v-if="item.community">
                {{ item.community }}
              </v-chip>
              <span v-if="item.serviceStatus">• {{ item.serviceStatus }}</span>
              <span v-if="item.branch"> • {{ item.branch }}</span>
              <span v-if="item.unitAgency"> • {{ item.unitAgency }}</span>
            </div>
          </template>

          <!-- Experience (NEW) -->
          <template #item.experience="{ item }">
            <div class="row-subtitle">
              <span v-if="(item.certifications || []).length">
                {{ (item.certifications || []).slice(0,2).join(', ') }}<span v-if="(item.certifications || []).length>2">…</span>
              </span>
              <span v-if="(item.skills || []).length">
                • {{ (item.skills || []).slice(0,2).join(', ') }}<span v-if="(item.skills || []).length>2">…</span>
              </span>
            </div>
          </template>

          <!-- Family (updated to Valorrr fields) -->
          <template #item.family="{ item }">
            <div class="row-subtitle">
              <v-chip x-small class="mr-1" color="primary" text-color="white" v-if="(item.dependentsAges || []).length">
                {{ (item.dependentsAges || []).length }} {{ (item.dependentsAges || []).length === 1 ? 'dependent' : 'dependents' }}
              </v-chip>
              <span v-if="item.familyStatus">• {{ item.familyStatus }}</span>
              <span v-if="(item.supportTopics || []).length">
                • {{ (item.supportTopics || []).slice(0,3).join(', ') }}<span v-if="(item.supportTopics || []).length>3">…</span>
              </span>
            </div>
          </template>

          <!-- Location -->
          <template #item.location="{ item }">
            <span class="row-subtitle">
              {{ item.city || '—' }}<span v-if="item.stateRegion">, {{ item.stateRegion }}</span><span v-if="item.country"> • {{ item.country }}</span>
            </span>
          </template>

          <!-- Prefs (NEW) -->
          <template #item.prefs="{ item }">
            <div class="row-subtitle">
              <span v-if="item.prefersVirtual !== undefined">{{ item.prefersVirtual ? 'Virtual OK' : 'In-person' }}</span>
              <span v-if="item.radiusKm != null"> • {{ item.radiusKm }}km</span>
              <span v-if="item.languages && item.languages.length"> • {{ item.languages.length }} lang</span>
            </div>
          </template>

          <!-- Visibility -->
          <template #item.visibility="{ item }">
            <v-chip x-small class="font-weight-bold" color="indigo" text-color="white">
              {{ item.profileVisibility || 'Members' }}
            </v-chip>
          </template>

          <!-- Consent -->
          <template #item.consent="{ item }">
            <v-icon :color="(item.communityConsent ?? item.privacyConsent) ? 'green' : 'grey'">
              {{ (item.communityConsent ?? item.privacyConsent) ? 'mdi-check-circle' : 'mdi-close-circle' }}
            </v-icon>
          </template>

          <!-- Profile -->
          <template #item.profile="{ item }">
            <v-chip
              x-small
              :color="item.profileCompletedAt ? 'green' : 'grey darken-1'"
              text-color="white"
              class="font-weight-bold"
            >
              {{ item.profileCompletedAt ? 'Complete' : 'Partial' }}
            </v-chip>
          </template>

          <!-- Status -->
          <template #item.status="{ item }">
            <v-chip x-small :color="statusColor(item.status)" text-color="white" class="font-weight-bold">
              {{ item.status || 'pending' }}
            </v-chip>
          </template>

          <!-- Created -->
          <template #item.createdAt="{ item }">
            <span class="row-subtitle">{{ fmt(item.createdAt) }}</span>
          </template>

          <!-- Actions -->
          <template #item.actions="{ item }">
            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <v-btn icon small v-bind="attrs" v-on="on" @click="openDetail(item)"><v-icon>mdi-eye</v-icon></v-btn>
              </template>
              <span>View</span>
            </v-tooltip>

            <v-tooltip top v-if="item.status !== 'approved'">
              <template #activator="{ on, attrs }">
                <v-btn icon small color="success" v-bind="attrs" v-on="on" @click="approve(item)">
                  <v-icon>mdi-check-circle</v-icon>
                </v-btn>
              </template>
              <span>Approve</span>
            </v-tooltip>

            <v-tooltip top v-if="item.status !== 'rejected'">
              <template #activator="{ on, attrs }">
                <v-btn icon small color="error" v-bind="attrs" v-on="on" @click="reject(item)">
                  <v-icon>mdi-close-circle</v-icon>
                </v-btn>
              </template>
              <span>Reject</span>
            </v-tooltip>

            <v-tooltip top>
              <template #activator="{ on, attrs }">
                <a :href="`mailto:${item.email}`" v-bind="attrs" v-on="on">
                  <v-btn icon small><v-icon>mdi-email-outline</v-icon></v-btn>
                </a>
              </template>
              <span>Email</span>
            </v-tooltip>
          </template>
        </v-data-table>

        <v-card-actions class="justify-center">
          <v-btn :disabled="!canLoadMore || loadingMore" @click="loadMore" text class="white--text">
            <v-icon left>mdi-chevron-down</v-icon> Load more
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>

    <!-- Detail Drawer -->
    <v-navigation-drawer
      v-model="detailOpen"
      right
      temporary
      stateless
      :scrim="false"
      :disable-resize-watcher="true"
      :disable-route-watcher="true"
      :touchless="true"
      app
      width="560"
      class="detail-drawer"
      eager
    >
      <v-toolbar flat dense class="drawer-bar" dark>
        <v-toolbar-title class="font-weight-bold">Applicant</v-toolbar-title>
        <v-spacer />
        <v-btn icon @click="detailOpen = false"><v-icon>mdi-close</v-icon></v-btn>
      </v-toolbar>

      <div v-if="detail" class="drawer-scroll px-4">
        <div class="d-flex align-center mb-3 mt-4">
          <v-avatar size="52" class="mr-3"><v-img :src="detail.image || detail.user?.image || defaultImage" /></v-avatar>
          <div>
            <div class="row-title">{{ detail.fullName }}</div>
            <div class="caption row-subtitle">{{ detail.email }}</div>
            <div class="caption row-subtitle" v-if="detail.username">@{{ detail.username }}</div>
          </div>
        </div>

        <v-tabs v-model="tab" background-color="transparent" dark>
          <v-tab>Overview</v-tab>
          <v-tab>Service</v-tab>
          <v-tab>Experience</v-tab>
          <v-tab>Family</v-tab>
          <v-tab>Preferences</v-tab>
          <v-tab>Activity</v-tab>
          <v-tab>Raw</v-tab>
        </v-tabs>

        <v-tabs-items v-model="tab">
          <!-- OVERVIEW -->
          <v-tab-item>
            <div class="py-3">
              <div class="mb-2">
                <v-chip small class="mr-2" :color="detail.profileCompletedAt ? 'green' : 'grey darken-1'" text-color="white">
                  <v-icon left small>mdi-account-check</v-icon>{{ detail.profileCompletedAt ? 'Profile complete' : 'Profile partial' }}
                </v-chip>
                <v-chip x-small :color="statusColor(detail.status)" text-color="white">{{ detail.status || 'pending' }}</v-chip>
              </div>

              <v-list dense two-line>
                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="white--text">Location</v-list-item-title>
                    <v-list-item-subtitle class="row-subtitle">
                      {{ detail.city || '—' }}<span v-if="detail.stateRegion">, {{ detail.stateRegion }}</span><span v-if="detail.country"> • {{ detail.country }}</span>
                      <span v-if="detail.zip"> • {{ detail.zip }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="white--text">Community</v-list-item-title>
                    <v-list-item-subtitle class="row-subtitle">
                      {{ detail.community || '—' }} <span v-if="detail.serviceStatus">• {{ detail.serviceStatus }}</span>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="white--text">Languages</v-list-item-title>
                    <v-list-item-subtitle class="row-subtitle">{{ (detail.languages || []).join(', ') || '—' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="white--text">Visibility</v-list-item-title>
                    <v-list-item-subtitle class="row-subtitle">{{ detail.profileVisibility || 'Members' }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-list-item>
                  <v-list-item-content>
                    <v-list-item-title class="white--text">Community consent</v-list-item-title>
                    <v-list-item-subtitle class="row-subtitle">
                      <v-icon :color="(detail.communityConsent ?? detail.privacyConsent) ? 'green' : 'grey'">
                        {{ (detail.communityConsent ?? detail.privacyConsent) ? 'mdi-check-circle' : 'mdi-close-circle' }}
                      </v-icon>
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>
          </v-tab-item>

          <!-- SERVICE -->
          <v-tab-item>
            <div class="py-3">
              <v-simple-table dense dark class="dark-surface mb-3">
                <tbody>
                  <tr><td class="white--text font-weight-bold pr-4">Community</td><td class="row-subtitle">{{ detail.community || '—' }}</td></tr>
                  <tr><td class="white--text font-weight-bold pr-4">Status</td><td class="row-subtitle">{{ detail.serviceStatus || '—' }}</td></tr>
                  <tr v-if="detail.branch"><td class="white--text font-weight-bold pr-4">Branch</td><td class="row-subtitle">{{ detail.branch }}</td></tr>
                  <tr v-if="detail.unitAgency"><td class="white--text font-weight-bold pr-4">Unit / Agency</td><td class="row-subtitle">{{ detail.unitAgency }}</td></tr>
                  <tr v-if="detail.stationBase"><td class="white--text font-weight-bold pr-4">Station / Base</td><td class="row-subtitle">{{ detail.stationBase }}</td></tr>
                  <tr v-if="detail.rank"><td class="white--text font-weight-bold pr-4">Rank / Paygrade</td><td class="row-subtitle">{{ detail.rank }}</td></tr>
                  <tr v-if="detail.mos || detail.jobCode"><td class="white--text font-weight-bold pr-4">MOS / Job</td><td class="row-subtitle">{{ detail.mos || detail.jobCode }}</td></tr>
                  <tr v-if="detail.yearsOfService != null"><td class="white--text font-weight-bold pr-4">Years of service</td><td class="row-subtitle">{{ detail.yearsOfService }}</td></tr>
                  <tr v-if="detail.clearanceLevel"><td class="white--text font-weight-bold pr-4">Security clearance</td><td class="row-subtitle">{{ detail.clearanceLevel }}</td></tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-tab-item>

          <!-- EXPERIENCE -->
          <v-tab-item>
            <div class="py-3">
              <div class="mb-3">
                <div class="white--text font-weight-bold mb-1">Certifications</div>
                <div v-if="(detail.certifications || []).length" class="d-flex flex-wrap">
                  <v-chip v-for="(c,i) in detail.certifications" :key="'c-'+i" x-small class="mr-1 mb-1">{{ c }}</v-chip>
                </div>
                <div v-else class="row-subtitle">—</div>
              </div>

              <div class="mb-3">
                <div class="white--text font-weight-bold mb-1">Skills</div>
                <div v-if="(detail.skills || []).length" class="d-flex flex-wrap">
                  <v-chip v-for="(s,i) in detail.skills" :key="'s-'+i" x-small class="mr-1 mb-1">{{ s }}</v-chip>
                </div>
                <div v-else class="row-subtitle">—</div>
              </div>

              <div>
                <div class="white--text font-weight-bold mb-1">Deployments / Assignments</div>
                <div v-if="(detail.deployments || []).length" class="d-flex flex-wrap">
                  <v-chip v-for="(d,i) in detail.deployments" :key="'d-'+i" x-small class="mr-1 mb-1">{{ d }}</v-chip>
                </div>
                <div v-else class="row-subtitle">—</div>
              </div>
            </div>
          </v-tab-item>

          <!-- FAMILY -->
          <v-tab-item>
            <div class="py-3">
              <v-simple-table dense dark class="dark-surface mb-3" v-if="detail.familyStatus || (detail.dependentsAges || []).length">
                <tbody>
                  <tr v-if="detail.familyStatus">
                    <td class="white--text font-weight-bold pr-4">Family status</td>
                    <td class="row-subtitle">{{ detail.familyStatus }}</td>
                  </tr>
                  <tr v-if="(detail.dependentsAges || []).length">
                    <td class="white--text font-weight-bold pr-4">Dependents’ ages</td>
                    <td class="row-subtitle">{{ (detail.dependentsAges || []).join(', ') }}</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div>
                <div class="white--text font-weight-bold mb-1">Support topics</div>
                <div v-if="(detail.supportTopics || []).length" class="d-flex flex-wrap">
                  <v-chip v-for="(it,k) in detail.supportTopics" :key="k" x-small class="mr-1 mb-1">{{ it }}</v-chip>
                </div>
                <div v-else class="row-subtitle">—</div>
              </div>
            </div>
          </v-tab-item>

          <!-- PREFERENCES -->
          <v-tab-item>
            <div class="py-3">
              <v-simple-table dense dark class="dark-surface mb-3">
                <tbody>
                  <tr><td class="white--text font-weight-bold pr-4">Prefer virtual</td><td class="row-subtitle">{{ detail.prefersVirtual ? 'Yes' : 'No' }}</td></tr>
                  <tr v-if="detail.radiusKm != null"><td class="white--text font-weight-bold pr-4">Match radius</td><td class="row-subtitle">{{ detail.radiusKm }} km</td></tr>
                  <tr v-if="detail.availability"><td class="white--text font-weight-bold pr-4">Availability</td><td class="row-subtitle">{{ detail.availability }}</td></tr>
                  <tr>
                    <td class="white--text font-weight-bold pr-4">Languages</td>
                    <td class="row-subtitle">{{ (detail.languages || []).join(', ') || '—' }}</td>
                  </tr>
                </tbody>
              </v-simple-table>

              <div class="mb-3">
                <div class="white--text font-weight-bold mb-1">Could mentor</div>
                <div v-if="(detail.mentorshipOffer || []).length" class="d-flex flex-wrap">
                  <v-chip v-for="(m,i) in detail.mentorshipOffer" :key="'mo-'+i" x-small class="mr-1 mb-1">{{ m }}</v-chip>
                </div>
                <div v-else class="row-subtitle">—</div>
              </div>

              <div>
                <div class="white--text font-weight-bold mb-1">Seeking mentorship</div>
                <div v-if="(detail.mentorshipSeek || []).length" class="d-flex flex-wrap">
                  <v-chip v-for="(m,i) in detail.mentorshipSeek" :key="'ms-'+i" x-small class="mr-1 mb-1">{{ m }}</v-chip>
                </div>
                <div v-else class="row-subtitle">—</div>
              </div>
            </div>
          </v-tab-item>

          <!-- ACTIVITY -->
          <v-tab-item>
            <div class="py-3">
              <v-simple-table dense dark class="dark-surface">
                <tbody>
                  <tr><td class="white--text font-weight-bold pr-4">Joined</td><td class="row-subtitle">{{ fmt(detail.createdAt) }}</td></tr>
                  <tr><td class="white--text font-weight-bold pr-4">Profile completed</td><td class="row-subtitle">{{ fmt(detail.profileCompletedAt) }}</td></tr>
                  <tr><td class="white--text font-weight-bold pr-4">Approved</td><td class="row-subtitle">{{ fmt(detail.approvedAt) }}</td></tr>
                  <tr><td class="white--text font-weight-bold pr-4">Rejected</td><td class="row-subtitle">{{ fmt(detail.rejectedAt) }}</td></tr>
                  <tr><td class="white--text font-weight-bold pr-4">Reviewer</td><td class="row-subtitle">{{ detail.reviewer || '—' }}</td></tr>
                  <tr><td class="white--text font-weight-bold pr-4">Wave</td><td class="row-subtitle">{{ detail.wave != null ? detail.wave : '—' }}</td></tr>
                  <tr><td class="white--text font-weight-bold pr-4">Referrer</td><td class="row-subtitle">{{ detail.referrer || '—' }}</td></tr>
                </tbody>
              </v-simple-table>
            </div>
          </v-tab-item>

          <!-- RAW -->
          <v-tab-item>
            <div class="py-3">
              <pre class="raw-pre">{{ rawJson }}</pre>
            </div>
          </v-tab-item>
        </v-tabs-items>

        <div class="mt-4 d-flex pb-4">
          <v-btn block class="mr-2" color="success" @click="approve(detail)" :loading="busyId === detail.uid">
            <v-icon left>mdi-check</v-icon> Approve
          </v-btn>
          <v-btn block color="error" @click="reject(detail)" :loading="busyId === detail.uid">
            <v-icon left>mdi-close</v-icon> Reject
          </v-btn>
        </div>
      </div>
    </v-navigation-drawer>

    <v-snackbar v-model="snack.show" :timeout="2500" :color="snack.color">{{ snack.text }}</v-snackbar>
  </v-container>
</template>

<script>
import {
  collection, query, orderBy, limit, startAfter, onSnapshot,
  doc, getDoc, setDoc, serverTimestamp, writeBatch, getDocs
} from "firebase/firestore";
import { db, auth } from "@/firebase";

export default {
  name: "AdminWaitlist",
  data() {
    return {
      loading: true,
      loadingMore: false,
      applicants: [],
      lastSnap: null,
      pageSize: 50,
      search: "",
      statusFilter: "pending",
      onlyCompleted: false,
      statusOptions: [
        { value: "pending",  label: "Pending",  icon: "mdi-timer-sand" },
        { value: "approved", label: "Approved", icon: "mdi-check-circle" },
        { value: "rejected", label: "Rejected", icon: "mdi-close-circle" },
        { value: "all",      label: "All",      icon: "mdi-format-list-bulleted" },
      ],
      headers: [
        { text: "Applicant", value: "fullName", sortable: false },
        { text: "Handle", value: "username", sortable: false },
        { text: "Role", value: "ownerRole", sortable: false },
        { text: "Service", value: "service", sortable: false },         // NEW
        { text: "Experience", value: "experience", sortable: false },   // NEW
        { text: "Family", value: "family", sortable: false },
        { text: "Location", value: "location", sortable: false },
        { text: "Prefs", value: "prefs", sortable: false },             // NEW
        { text: "Visibility", value: "visibility", sortable: false },
        { text: "Consent", value: "consent", sortable: false },
        { text: "Profile", value: "profile", sortable: false },
        { text: "Status", value: "status", sortable: false },
        { text: "Joined", value: "createdAt", sortable: false },
        { text: "", value: "actions", sortable: false, align: "end" },
      ],
      defaultImage: require("@/assets/logo.png"),
      detailOpen: false,
      detail: null,
      tab: 0,
      busyId: null,
      snack: { show: false, text: "", color: "info" },
      unsub: null,
    };
  },
  computed: {
    filteredApplicants() {
      let items = this.applicants;
      if (this.statusFilter !== "all") {
        items = items.filter(a => (a.status || "pending") === this.statusFilter);
      }
      if (this.onlyCompleted) items = items.filter(a => !!a.profileCompletedAt);
      if (this.search) {
        const s = this.search.toLowerCase();
        items = items.filter(a =>
          (a.fullName || "").toLowerCase().includes(s) ||
          (a.email || "").toLowerCase().includes(s) ||
          (a.username || "").toLowerCase().includes(s) ||
          (a.ownerRole || "").toLowerCase().includes(s) ||
          (a.city || "").toLowerCase().includes(s) ||
          (a.stateRegion || "").toLowerCase().includes(s) ||
          (a.country || "").toLowerCase().includes(s) ||
          (a.community || "").toLowerCase().includes(s) ||
          (a.serviceStatus || "").toLowerCase().includes(s) ||
          (a.branch || "").toLowerCase().includes(s) ||
          (a.unitAgency || "").toLowerCase().includes(s) ||
          (a.stationBase || "").toLowerCase().includes(s) ||
          (a.rank || "").toLowerCase().includes(s) ||
          (a.mos || a.jobCode || "").toLowerCase().includes(s) ||
          String(a.yearsOfService || "").toLowerCase().includes(s) ||
          (a.clearanceLevel || "").toLowerCase().includes(s) ||
          (a.familyStatus || "").toLowerCase().includes(s) ||
          (a.dependentsAges || []).join(", ").toLowerCase().includes(s) ||
          (a.supportTopics || []).join(", ").toLowerCase().includes(s) ||
          (a.certifications || []).join(", ").toLowerCase().includes(s) ||
          (a.skills || []).join(", ").toLowerCase().includes(s) ||
          (a.deployments || []).join(", ").toLowerCase().includes(s) ||
          (a.languages || []).join(", ").toLowerCase().includes(s) ||
          (a.availability || "").toLowerCase().includes(s)
        );
      }
      return items;
    },
    counts() {
      const c = { all: this.applicants.length, pending: 0, approved: 0, rejected: 0 };
      this.applicants.forEach(a => { const st = (a.status || "pending"); if (c[st] !== undefined) c[st]++; });
      return c;
    },
    canLoadMore() { return !!this.lastSnap; },
    rawJson() {
      if (!this.detail) return "";
      const safe = {
        waitlist: { ...this.detail, user: undefined },
        user: this.detail.user || null
      };
      return JSON.stringify(safe, (k, v) => (v && v.toDate ? v.toDate().toISOString() : v), 2);
    }
  },
  methods: {
    initials(name) {
      const [a = "", b = ""] = (name || "").trim().split(/\s+/, 2);
      return (a[0] || "?").toUpperCase() + (b[0] || "").toUpperCase();
    },
    toast(text, color = "info") { this.snack = { show: true, text, color }; },

    attachLiveQuery() {
      // Firestore returns full docs; we render all newly added fields
      const qy = query(collection(db, "waitlist"), orderBy("createdAt", "desc"), limit(this.pageSize));
      if (this.unsub) this.unsub();
      this.unsub = onSnapshot(qy, (snap) => {
        this.applicants = snap.docs.map(d => ({ uid: d.id, ...d.data() }));
        this.lastSnap = snap.docs.length === this.pageSize ? snap.docs[snap.docs.length - 1] : null;
        this.loading = false;
      }, (err) => { console.error(err); this.toast("Failed to load waitlist", "error"); this.loading = false; });
    },

    async loadMore() {
      if (!this.lastSnap) return;
      this.loadingMore = true;
      const qy = query(collection(db, "waitlist"), orderBy("createdAt", "desc"), startAfter(this.lastSnap), limit(this.pageSize));
      const snap = await getDocs(qy);
      this.applicants = [...this.applicants, ...snap.docs.map(d => ({ uid: d.id, ...d.data() }))];
      this.lastSnap = snap.docs.length === this.pageSize ? snap.docs[snap.docs.length - 1] : null;
      this.loadingMore = false;
    },

    async openDetail(rowItem) {
      try {
        this.tab = 0;
        const wDoc = await getDoc(doc(db, "waitlist", rowItem.uid));
        const uDoc = await getDoc(doc(db, "user", rowItem.uid));
        const waitData = wDoc.exists() ? wDoc.data() : rowItem;
        this.detail = {
          uid: rowItem.uid,
          ...waitData,
          user: uDoc.exists() ? uDoc.data() : null
        };
        this.detailOpen = true;
      } catch (e) {
        console.error(e);
        this.toast("Could not open detail", "error");
      }
    },

    statusColor(st) { const s = (st || "pending"); return s === "approved" ? "green" : s === "rejected" ? "red" : "amber"; },
    fmt(ts) {
      if (!ts) return "—";
      try { return ts.toDate ? ts.toDate().toLocaleString() : new Date(ts).toLocaleString(); }
      catch { return "—"; }
    },

    async approve(item) {
      try {
        this.busyId = item.uid;
        const batch = writeBatch(db);
        batch.update(doc(db, "waitlist", item.uid), { status: "approved", approvedAt: serverTimestamp(), reviewer: auth.currentUser?.uid || null });
        batch.set(doc(db, "user", item.uid), {
          uid: item.uid, fullName: item.fullName || "", email: item.email || "",
          role: "Member", type: "Member", status: "approved", approvedAt: serverTimestamp(),
        }, { merge: true });
        await batch.commit(); this.toast("Approved ✔️", "success");
      } catch (e) { console.error(e); this.toast("Approval failed", "error"); } finally { this.busyId = null; }
    },

    async reject(item) {
      try {
        this.busyId = item.uid;
        const batch = writeBatch(db);
        batch.update(doc(db, "waitlist", item.uid), { status: "rejected", rejectedAt: serverTimestamp(), reviewer: auth.currentUser?.uid || null });
        batch.set(doc(db, "user", item.uid), { uid: item.uid, fullName: item.fullName || "", email: item.email || "", status: "rejected", rejectedAt: serverTimestamp() }, { merge: true });
        await batch.commit(); this.toast("Rejected", "info");
      } catch (e) { console.error(e); this.toast("Reject failed", "error"); } finally { this.busyId = null; }
    },

    exportCsv() {
      const cols = [
        // Identity
        "uid","fullName","email","username","ownerRole",
        // Location
        "city","stateRegion","zip","country",
        // Service profile
        "community","serviceStatus","branch","unitAgency","stationBase","rank","mos_or_jobCode","yearsOfService","clearanceLevel",
        // Experience
        "certifications","skills","deployments",
        // Family & support
        "familyStatus","dependentsAges","supportTopics",
        // Preferences
        "prefersVirtual","radiusKm","availability","languages","mentorshipOffer","mentorshipSeek",
        // Other
        "profileVisibility","communityConsent",
        // Meta
        "status","createdAt","profileCompletedAt","approvedAt","rejectedAt","wave","referrer"
      ];
      const esc = v => v == null ? "" : String(v).replace(/"/g,'""');
      const wrap = v => `"${esc(v)}"`;
      const rows = this.filteredApplicants.map(a => {
        return [
          a.uid, wrap(a.fullName), wrap(a.email), wrap(a.username), wrap(a.ownerRole),
          wrap(a.city), wrap(a.stateRegion), wrap(a.zip), wrap(a.country),
          wrap(a.community), wrap(a.serviceStatus), wrap(a.branch), wrap(a.unitAgency), wrap(a.stationBase), wrap(a.rank),
          wrap(a.mos || a.jobCode), a.yearsOfService != null ? a.yearsOfService : "",
          wrap(a.clearanceLevel),
          wrap((a.certifications || []).join("; ")), wrap((a.skills || []).join("; ")), wrap((a.deployments || []).join("; ")),
          wrap(a.familyStatus), wrap((a.dependentsAges || []).join("; ")), wrap((a.supportTopics || []).join("; ")),
          a.prefersVirtual ? "true" : "false", a.radiusKm != null ? a.radiusKm : "",
          wrap(a.availability), wrap((a.languages || []).join("; ")), wrap((a.mentorshipOffer || []).join("; ")), wrap((a.mentorshipSeek || []).join("; ")),
          wrap(a.profileVisibility), (a.communityConsent ?? a.privacyConsent) ? "true" : "false",
          a.status ?? "pending", this.fmt(a.createdAt), this.fmt(a.profileCompletedAt), this.fmt(a.approvedAt), this.fmt(a.rejectedAt), a.wave ?? "", wrap(a.referrer)
        ].join(",");
      });
      const csv = [cols.join(","), ...rows].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = `waitlist-${new Date().toISOString().slice(0,10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    },

    refresh() { this.loading = true; this.attachLiveQuery(); },
  },
  mounted() { this.attachLiveQuery(); },
  beforeDestroy() { if (this.unsub) this.unsub(); }
};
</script>

<style scoped>
.admin-wl-wrap { min-height: 100vh; background: linear-gradient(180deg, #4B60C6 0%, #000000 100%); }
.grad-appbar { background: linear-gradient(135deg, #4B60C6, #000000); }

/* Surfaces */
.soft-card { box-shadow: 0 12px 26px rgba(0,0,0,.14); border-radius: 16px; }
.dark-surface { background: rgba(16,16,16,.84); border: 1px solid rgba(255,255,255,.08); }

/* Inputs / chips */
.search-input { max-width: 420px; }
.contrast-input ::v-deep .v-input__slot { background: rgba(0,0,0,.38) !important; }
.contrast-input ::v-deep input::placeholder { color: rgba(255,255,255,.8) !important; }
.chip-btn { background: rgba(255,255,255,.18); color:#4B60C6; text-transform:none; font-weight:700; }
.chip-btn:hover { background: rgba(255,255,255,.24); }
.badge { font-size: 11px; background: rgba(0,0,0,.28); padding: 2px 8px; border-radius: 999px; color: #fff; }

/* Data table (dark) */
.wl-table { color: white; }
.wl-table ::v-deep .v-data-table__wrapper { background: transparent !important; }
.wl-table ::v-deep thead th { background: transparent !important; color: #fff !important; font-weight: 800; }
.wl-table ::v-deep tbody tr td { color: #fff !important; }
.wl-table ::v-deep tbody tr:hover { background: rgba(255,255,255,.06) !important; }

/* Drawer */
.detail-drawer { background: linear-gradient(180deg,#1e1e1e,#111); color:#fff; z-index: 3000; }
.detail-drawer ::v-deep .v-tabs,
.detail-drawer ::v-deep .v-tabs-items,
.detail-drawer ::v-deep .v-list,
.detail-drawer ::v-deep .v-card,
.detail-drawer ::v-deep .v-simple-table { background: transparent !important; }
.detail-drawer ::v-deep .v-tab { text-transform:none; font-weight:700; }
.drawer-scroll { max-height: calc(100vh - 112px); overflow-y: auto; padding-bottom: 16px; }

/* Raw JSON block */
.raw-pre { white-space: pre-wrap; word-break: break-word; background: rgba(0,0,0,.35); padding: 12px; border-radius: 8px; }

.drawer-bar { background: linear-gradient(135deg, #4B60C6, #000000); }

/* Row text helpers */
.row-title { font-weight: 800; line-height: 1.1; }
.row-subtitle { color: white !important; }

.link { color: #fff; text-decoration: underline; }
.white--text { color: white !important; }
.opacity-80 { opacity: .8; }
.avatar-fallback { display:flex; align-items:center; justify-content:center; width:100%; height:100%; background:#234; color:#fff; font-weight:800; }
</style>
